import { type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchAttendanceSummary, fetchTaskSummary } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { todayInZone } from '../lib/attendance-session';
import { tabAwal, tabTerlihat } from '../lib/laporan-tabs';
import { ErrorCard, LoadingState } from '../states';
import { AttendanceDashboardPage } from './AttendanceDashboardPage';
import { DeliveryBoardPage } from './DeliveryBoardPage';
import { MonthlyAttendancePage } from './MonthlyAttendancePage';
import { TaskDashboardPage } from './TaskDashboardPage';

/**
 * Laporan — SATU pintu, isinya tab.
 *
 * ADA KARENA LAPORAN TERSEBAR. Sebelumnya empat layar laporan berdiri sendiri-sendiri di menu,
 * dengan nama yang mirip satu sama lain — orang harus menebak mana yang memuat angka yang
 * dicarinya, dan menutup tiga layar untuk membandingkan dua angka. Owner membandingkannya dengan
 * Reports di CRM dan memintanya disamakan: "semua terlihat dalam satu fitur".
 *
 * Layar-layar lamanya DIPAKAI ULANG apa adanya sebagai isi tab, bukan ditulis ulang. Masing-masing
 * sudah membawa aturan yang mahal dipelajari — angka apa adanya dari server, peringatan lokasi
 * yang wajib terlihat, ekspor yang mengikuti saringan. Menyalinnya ke halaman baru berarti
 * memindahkan seluruh aturan itu ke tempat kedua yang lalu menyimpang diam-diam.
 *
 * Rutenya yang lama TETAP HIDUP. Halaman detail hari menautkan balik ke `/dashboard/attendance`
 * bagi manajer yang membuka catatan orang lain — mematikan rute itu akan memutus tautan balik
 * justru pada layar yang paling sering dibuka dari sana.
 */
export function LaporanPage(): ReactNode {
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const terlihat = tabTerlihat(session.me.permissions);
  const aktif = tabAwal(session.me.permissions, params.get('tab'));

  if (aktif === null) {
    return (
      <section className="card">
        <h2>Laporan</h2>
        <p className="muted small">
          Peran Anda tidak memiliki akses ke laporan mana pun. Hubungi manajer bila ini keliru.
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <h2>Laporan</h2>
        <p className="muted small">Absensi, tugas, dan pengiriman dalam satu tempat.</p>
        {/*
          Tab sebagai TOMBOL, bukan tautan: tab yang berpindah lewat navigasi router akan
          memuat ulang seluruh pohon dan mengosongkan saringan yang sedang dipakai di dalamnya.
          Alamatnya tetap dapat dibagikan karena `?tab=` ikut ditulis.
        */}
        <div className="tab-bar" role="tablist" aria-label="Jenis laporan">
          {terlihat.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === aktif}
              className={tab.key === aktif ? 'tab tab-aktif' : 'tab'}
              onClick={() => setParams({ tab: tab.key }, { replace: true })}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/*
        Panel yang tidak aktif TIDAK dirender sama sekali, bukan disembunyikan dengan CSS.
        Merendernya semua berarti setiap kali Laporan dibuka ia menembak seluruh endpoint
        laporan sekaligus — beban yang tidak diminta siapa pun, di ponsel dan di server.
      */}
      {aktif === 'ringkasan' ? <Ringkasan /> : null}
      {aktif === 'absensi' ? <AttendanceDashboardPage /> : null}
      {aktif === 'bulanan' ? <MonthlyAttendancePage /> : null}
      {aktif === 'tugas' ? <TaskDashboardPage /> : null}
      {aktif === 'pengiriman' ? <DeliveryBoardPage /> : null}
    </>
  );
}

/**
 * Ringkasan — angka hari ini, tanpa memaksa memilih apa pun lebih dulu.
 *
 * Hanya menampilkan yang boleh dilihat pemanggilnya. Kartu yang ditampilkan lalu gagal memuat
 * karena izin lebih buruk daripada kartu yang tidak ada: yang pertama terlihat seperti sistem
 * rusak, yang kedua seperti batas yang memang begitu.
 *
 * DIEKSPOR — dipakai ulang apa adanya di beranda Owner/Manajer (item 5, review 12 Sep 2026),
 * bukan ditulis ulang. `fetchAttendanceSummary`/`fetchTaskSummary` SUDAH scope-aware (server
 * menentukan cakupan dari peran pemanggil — Manajer timnya, Owner seluruh organisasi), jadi
 * menaruhnya di dua layar tidak menaruh DUA aturan cakupan yang bisa menyimpang, hanya dua
 * tempat yang memanggil satu aturan yang sama.
 */
export function Ringkasan(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const izin = new Set(session.me.permissions);
  const superuser = izin.has('workforce.superuser');
  const bolehAbsensi = superuser || izin.has('workforce.attendance.read');
  const bolehTugas = superuser || izin.has('workforce.task.read');

  const { state, reload } = useAsync(async () => {
    const tanggal = todayInZone(new Date(), timezone);
    /*
     * Yang tidak boleh dilihat TIDAK DIPANGGIL — bukan dipanggil lalu galatnya ditelan. Panggilan
     * yang pasti ditolak hanya menghasilkan 403 di log yang membuat gangguan sungguhan lebih
     * sulit ditemukan.
     */
    const [absensi, tugas] = await Promise.all([
      bolehAbsensi ? fetchAttendanceSummary(session.auth, tanggal) : Promise.resolve(null),
      bolehTugas ? fetchTaskSummary(session.auth) : Promise.resolve(null),
    ]);
    return { absensi, tugas, tanggal };
  }, [session.auth, timezone, bolehAbsensi, bolehTugas]);

  if (state.status === 'loading') return <LoadingState label="Memuat ringkasan…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const { absensi, tugas } = state.data;

  return (
    <>
      {absensi ? (
        <section className="card">
          <h3>Absensi hari ini</h3>
          {/*
            SETIAP sel menuju tab Absensi (item 5, review 12 Sep 2026) — bukan enam tujuan
            berbeda per status. `AttendanceDashboardPage` sudah mengelompokkan roster PERSIS
            menurut status yang sama (NONE/LATE/PRESENT/INCOMPLETE/ON_LEAVE di
            `attendance-roster.ts`), jadi satu ketukan sudah menjawab "siapa saja" untuk keenamnya
            — menautkan ke enam URL berstatus yang belum ada berarti mengarang rute yang tidak
            benar-benar memfilter apa pun di sana.
          */}
          <div className="board">
            <Sel label="Hadir" value={absensi.present} tone="green" to="/laporan?tab=absensi" />
            <Sel label="Terlambat" value={absensi.late} tone="amber" to="/laporan?tab=absensi" />
            <Sel
              label="Belum absen masuk"
              value={absensi.notCheckedIn}
              tone="pink"
              to="/laporan?tab=absensi"
            />
            <Sel
              label="Kerja lapangan"
              value={absensi.fieldWork}
              tone="blue"
              to="/laporan?tab=absensi"
            />
            <Sel label="Izin" value={absensi.onLeave} tone="purple" to="/laporan?tab=absensi" />
            <Sel label="Tidak hadir" value={absensi.absent} to="/laporan?tab=absensi" />
          </div>
          {absensi.unverifiedLocationCount > 0 ? (
            /*
              Peringatan lokasi ikut ke Ringkasan, bukan hanya tab Absensi. Ringkasan adalah layar
              yang paling sering dibuka sekilas, dan angka "Hadir" yang rapi padahal separuhnya
              tanpa verifikasi lokasi adalah persis kesan keliru yang paling mahal.
            */
            <p className="warning">
              {absensi.unverifiedLocationCount} event tanpa verifikasi lokasi penuh hari ini.
            </p>
          ) : null}
          {absensi.pendingCorrectionCount > 0 ? (
            <p className="muted small">
              <Link to="/attendance/corrections">
                {absensi.pendingCorrectionCount} pengajuan koreksi menunggu keputusan
              </Link>
            </p>
          ) : null}
        </section>
      ) : null}

      {tugas ? (
        <section className="card">
          <h3>Tugas</h3>
          {/* Sama seperti Absensi di atas — satu tujuan (tab Tugas) yang SUDAH menampilkan
              keenam angka ini bersama daftar tugasnya, bukan enam URL berstatus yang tak ada. */}
          <div className="board">
            <Sel label="Ditugaskan" value={tugas.assigned} tone="blue" to="/laporan?tab=tugas" />
            <Sel label="Berjalan" value={tugas.started} tone="amber" to="/laporan?tab=tugas" />
            <Sel label="Sampai" value={tugas.arrived} tone="purple" to="/laporan?tab=tugas" />
            <Sel label="Selesai" value={tugas.completed} tone="green" to="/laporan?tab=tugas" />
            <Sel label="Gagal" value={tugas.failed} tone="pink" to="/laporan?tab=tugas" />
            <Sel label="Draf" value={tugas.draft} to="/laporan?tab=tugas" />
          </div>
        </section>
      ) : null}

      {!absensi && !tugas ? (
        <section className="card">
          <p className="muted small">Tidak ada angka yang boleh ditampilkan untuk peran Anda.</p>
        </section>
      ) : null}
    </>
  );
}

/**
 * Sel angka — bentuk yang sama dengan dashboard absensi, sengaja tidak dibuat varian baru.
 *
 * `to` opsional (item 5, review 12 Sep 2026): dengan tujuan, sel menjadi tautan utuh — target
 * sentuh selebar seluruh sel, bukan hanya angkanya. Tanpa `to`, tetap `<div>` biasa — dipakai
 * `AttendanceDashboardPage`/`TaskDashboardPage` yang sudah punya cara sendiri menampilkan
 * detailnya di bawah papan yang sama, jadi tidak butuh tautan berputar ke dirinya sendiri.
 */
function Sel({
  label,
  value,
  tone,
  to,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: 'blue' | 'amber' | 'green' | 'purple' | 'pink';
  readonly to?: string;
}): ReactNode {
  const kelas = tone ? `board-cell tone-${tone}` : 'board-cell';
  const isi = (
    <>
      <span className="board-count">{value}</span>
      <span className="muted small">{label}</span>
    </>
  );
  if (to) {
    return (
      <Link to={to} className={`${kelas} board-cell-link`}>
        {isi}
      </Link>
    );
  }
  return <div className={kelas}>{isi}</div>;
}
